import { gql, useApolloClient } from "@apollo/client";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Pages } from "../../../utils";
import sealImage from "../../../assets/home-page/authorized-interior-designer-seal.png";
import atlantaResidencePreview from "../../../assets/home-page/projects/Atlanta_Residence_-_AHL_Summer_Issue_2023.jpg";
import buckheadHomePreview from "../../../assets/home-page/projects/Buckhead_Home.jpg";
import collectedRetreatPreview from "../../../assets/home-page/projects/Collected_Retreat.jpg";
import highGlossPreview from "../../../assets/home-page/projects/High-Gloss_Ethereal_Kitchen_Breakfast_Room__Wine_Cellar.jpg";
import holidayShowhousePreview from "../../../assets/home-page/projects/Holiday_Showhouse_2025.jpg";
import ladiesDressingRoomPreview from "../../../assets/home-page/projects/Ladies_Dressing_Room.jpg";
import nearTearDownPreview from "../../../assets/home-page/projects/Near-Tear-Down_Reno.jpg";
import sophisticatedSimplicityPreview from "../../../assets/home-page/projects/Sophisticated_Simplicity_Renovation.jpg";
import artistsAtelierPreview from "../../../assets/home-page/projects/The_Artists_Atelier.jpg";
import { ProjectPreviewDialog } from "../../../components/project-preview-dialog";
import {
  DesignerFileCardApi,
  useGetDesignerFileCards,
} from "../../../hooks/use-get-designer-file-cards";
import { useCarousel } from "./use-carousel";

type LandingPreviewSeed = {
  key: string;
  fileStem: string;
  imageUrl: string;
};

type ResolvedLandingProjectCard = {
  key: string;
  title: string;
  imageUrl: string;
  projectId: string | null;
  designerId: string | null;
  displayDesignerId: string | null;
};

type DesignerAliasByIdData = {
  getDesignerBio?: {
    designerId?: {
      aliasId?: string | null;
    } | null;
  } | null;
};

type DesignerAliasByIdVars = {
  designerId: string;
};

type DesignerDirectoryData = {
  getAllDesigners?: Array<{
    _id?: string | null;
    aliasId?: string | null;
  } | null> | null;
};

const PROJECT_PREVIEW_SEEDS: LandingPreviewSeed[] = [
  {
    key: "atlanta-residence-ahl-summer-issue-2023",
    fileStem: "Atlanta_Residence_-_AHL_Summer_Issue_2023",
    imageUrl: atlantaResidencePreview,
  },
  {
    key: "buckhead-home",
    fileStem: "Buckhead_Home",
    imageUrl: buckheadHomePreview,
  },
  {
    key: "collected-retreat",
    fileStem: "Collected_Retreat",
    imageUrl: collectedRetreatPreview,
  },
  {
    key: "high-gloss-ethereal-kitchen-breakfast-room-wine-cellar",
    fileStem: "High-Gloss_Ethereal_Kitchen_Breakfast_Room__Wine_Cellar",
    imageUrl: highGlossPreview,
  },
  {
    key: "holiday-showhouse-2025",
    fileStem: "Holiday_Showhouse_2025",
    imageUrl: holidayShowhousePreview,
  },
  {
    key: "ladies-dressing-room",
    fileStem: "Ladies_Dressing_Room",
    imageUrl: ladiesDressingRoomPreview,
  },
  {
    key: "near-tear-down-reno",
    fileStem: "Near-Tear-Down_Reno",
    imageUrl: nearTearDownPreview,
  },
  {
    key: "sophisticated-simplicity-renovation",
    fileStem: "Sophisticated_Simplicity_Renovation",
    imageUrl: sophisticatedSimplicityPreview,
  },
  {
    key: "the-artists-atelier",
    fileStem: "The_Artists_Atelier",
    imageUrl: artistsAtelierPreview,
  },
];

const PROJECT_FETCH_PAGE_SIZE = 120;
const PROJECT_FETCH_MAX_PAGES = 25;
const MONGO_ID_REGEX = /^[a-f0-9]{24}$/i;
const FEATURED_PROJECT_CARD_KEY = "near-tear-down-reno";
const FEATURED_DESIGNER_ALIAS = "DESIGNER_D3200C16339";

const DESIGNER_ID_OVERRIDES_BY_CARD_KEY: Record<string, string> = {
  [FEATURED_PROJECT_CARD_KEY]: FEATURED_DESIGNER_ALIAS,
};

const GET_DESIGNER_ALIAS_BY_ID = gql`
  query GetDesignerAliasById($designerId: String!) {
    getDesignerBio(designerId: $designerId) {
      designerId {
        aliasId
      }
    }
  }
`;

const GET_DESIGNER_DIRECTORY = gql`
  query GetAllDesignersForLandingCards {
    getAllDesigners {
      _id
      aliasId
    }
  }
`;

const normalizeTitle = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[_-]+/g, " ")
    .replace(/['’`]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toDisplayTitle = (fileStem: string) =>
  fileStem
    .replace(/__/g, " & ")
    .replace(/_/g, " ")
    .replace(/\s+-\s+/g, " - ")
    .replace(/\s+/g, " ")
    .trim();

const scoreTitleMatch = (seedTitle: string, candidateTitle: string) => {
  const seed = normalizeTitle(seedTitle);
  const candidate = normalizeTitle(candidateTitle);

  if (!seed || !candidate) return Number.NEGATIVE_INFINITY;
  if (seed === candidate) return 10_000;

  let score = 0;

  if (seed.includes(candidate) || candidate.includes(seed)) {
    score += 250;
  }

  const seedTokens = seed.split(" ");
  const candidateTokens = new Set(candidate.split(" "));
  const commonTokenCount = seedTokens.filter((token) => candidateTokens.has(token))
    .length;

  score += commonTokenCount * 20;
  score -= Math.abs(seedTokens.length - candidateTokens.size) * 2;

  return score;
};

const getCardTitleCandidate = (card: DesignerFileCardApi) =>
  (card.title || card.image?.title || "").trim();

const getAliasSuffix = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(/^(?:designer_|match_)?(.+)$/i);
  const suffix = match?.[1]?.trim();
  if (!suffix) return null;
  return suffix.toLowerCase();
};

const toDesignerDisplayId = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (MONGO_ID_REGEX.test(trimmed)) {
    return `DESIGNER_${trimmed.slice(-10).toUpperCase()}`;
  }

  const match = trimmed.match(/^(?:designer_|match_)?(.+)$/i);
  const suffix = match?.[1]?.trim();
  if (!suffix) return null;

  return `DESIGNER_${suffix.toUpperCase()}`;
};

const ArrowLeft = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M11 3.5L5.5 9L11 14.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRight = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M7 3.5L12.5 9L7 14.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DesignerFilesSection = () => {
  const apolloClient = useApolloClient();
  const { getProjectFileCards } = useGetDesignerFileCards();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewProjectId, setPreviewProjectId] = useState<string | null>(null);
  const [resolvedCards, setResolvedCards] = useState<ResolvedLandingProjectCard[]>(
    () =>
      PROJECT_PREVIEW_SEEDS.map((seed) => ({
        key: seed.key,
        title: toDisplayTitle(seed.fileStem),
        imageUrl: seed.imageUrl,
        projectId: null,
        designerId: null,
        displayDesignerId: DESIGNER_ID_OVERRIDES_BY_CARD_KEY[seed.key] ?? null,
      }))
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchAndResolveCards = async () => {
      const allProjectCards: DesignerFileCardApi[] = [];
      let offset = 0;
      let designerDirectory: Array<{ _id?: string | null; aliasId?: string | null }> =
        [];

      try {
        const response = await apolloClient.query<DesignerDirectoryData>({
          query: GET_DESIGNER_DIRECTORY,
          fetchPolicy: "cache-first",
        });

        designerDirectory = (response.data?.getAllDesigners ?? []).filter(
          (designer): designer is { _id?: string | null; aliasId?: string | null } =>
            Boolean(designer)
        );
      } catch {
        designerDirectory = [];
      }

      try {
        for (let page = 0; page < PROJECT_FETCH_MAX_PAGES; page += 1) {
          const cards = await getProjectFileCards(offset, PROJECT_FETCH_PAGE_SIZE);
          if (!cards.length) break;
          allProjectCards.push(...cards);
          offset += cards.length;
          if (cards.length < PROJECT_FETCH_PAGE_SIZE) break;
        }
      } catch (error) {
        if (isCancelled) return;
        setResolvedCards(
          PROJECT_PREVIEW_SEEDS.map((seed) => ({
            key: seed.key,
            title: toDisplayTitle(seed.fileStem),
            imageUrl: seed.imageUrl,
            projectId: null,
            designerId: null,
            displayDesignerId: DESIGNER_ID_OVERRIDES_BY_CARD_KEY[seed.key] ?? null,
          }))
        );
        return;
      }

      const availableCards = allProjectCards.filter((card) => {
        if (card.published === false) return false;
        return Boolean(card.fileId && getCardTitleCandidate(card));
      });

      const aliasByDesignerIdFromDirectory = new Map<string, string>();
      const designerIdByAliasSuffix = new Map<string, string>();

      designerDirectory.forEach((designer) => {
        const designerObjectId = String(designer?._id ?? "").trim();
        const aliasId = String(designer?.aliasId ?? "").trim();
        const aliasSuffix = getAliasSuffix(aliasId);

        if (!designerObjectId || !aliasSuffix) return;

        aliasByDesignerIdFromDirectory.set(designerObjectId, aliasId);
        designerIdByAliasSuffix.set(aliasSuffix, designerObjectId);
      });

      const featuredAliasSuffix = getAliasSuffix(FEATURED_DESIGNER_ALIAS);
      const featuredDesignerObjectId =
        featuredAliasSuffix
          ? designerIdByAliasSuffix.get(featuredAliasSuffix) ?? null
          : null;

      const featuredDesignerCards = availableCards.filter((card) => {
        const cardDesignerId = String(card.designerId ?? "").trim();
        if (!cardDesignerId) return false;

        if (featuredDesignerObjectId && cardDesignerId === featuredDesignerObjectId) {
          return true;
        }

        const cardAliasSuffix = getAliasSuffix(cardDesignerId);
        return Boolean(
          featuredAliasSuffix &&
            cardAliasSuffix &&
            cardAliasSuffix === featuredAliasSuffix
        );
      });

      const usedProjectIds = new Set<string>();
      const nextResolvedCards = PROJECT_PREVIEW_SEEDS.map((seed) => {
        const seedTitle = toDisplayTitle(seed.fileStem);
        const candidatePool =
          seed.key === FEATURED_PROJECT_CARD_KEY && featuredDesignerCards.length
            ? featuredDesignerCards
            : availableCards;

        const rankedMatch = candidatePool
          .filter((card) => !usedProjectIds.has(String(card.fileId)))
          .map((card) => ({
            card,
            score: scoreTitleMatch(seedTitle, getCardTitleCandidate(card)),
          }))
          .filter(({ score }) => Number.isFinite(score) && score > 40)
          .sort((left, right) => right.score - left.score)[0];

        const selectedCard =
          rankedMatch?.card ??
          candidatePool.find((card) => !usedProjectIds.has(String(card.fileId))) ??
          null;

        if (selectedCard?.fileId) {
          usedProjectIds.add(String(selectedCard.fileId));
        }

        return {
          key: seed.key,
          // Keep homepage thumbnails and labels pinned to local curated assets.
          title: seedTitle,
          imageUrl: seed.imageUrl,
          projectId: selectedCard?.fileId ? String(selectedCard.fileId) : null,
          designerId: selectedCard?.designerId
            ? String(selectedCard.designerId)
            : null,
          displayDesignerId: DESIGNER_ID_OVERRIDES_BY_CARD_KEY[seed.key] ?? null,
        };
      });

      const uniqueDesignerIds = Array.from(
        new Set(
          nextResolvedCards
            .map((card) => card.designerId)
            .filter((designerId): designerId is string => Boolean(designerId))
        )
      );

      const aliasByDesignerId = new Map<string, string | null>();
      uniqueDesignerIds.forEach((designerId) => {
        aliasByDesignerId.set(
          designerId,
          aliasByDesignerIdFromDirectory.get(designerId) ?? null
        );
      });

      const unresolvedDesignerIds = uniqueDesignerIds.filter(
        (designerId) => !aliasByDesignerId.get(designerId)
      );

      await Promise.all(
        unresolvedDesignerIds.map(async (designerId) => {
          try {
            const response = await apolloClient.query<
              DesignerAliasByIdData,
              DesignerAliasByIdVars
            >({
              query: GET_DESIGNER_ALIAS_BY_ID,
              variables: { designerId },
              fetchPolicy: "cache-first",
            });

            aliasByDesignerId.set(
              designerId,
              response.data?.getDesignerBio?.designerId?.aliasId ?? null
            );
          } catch {
            aliasByDesignerId.set(designerId, null);
          }
        })
      );

      const normalizedCards = nextResolvedCards.map((card) => {
        const override = DESIGNER_ID_OVERRIDES_BY_CARD_KEY[card.key] ?? null;
        const aliasId = card.designerId ? aliasByDesignerId.get(card.designerId) : null;
        const displayDesignerId =
          override ??
          toDesignerDisplayId(aliasId ?? card.designerId) ??
          null;

        return {
          ...card,
          displayDesignerId,
        };
      });

      if (!isCancelled) {
        setResolvedCards(normalizedCards);
      }
    };

    fetchAndResolveCards();

    return () => {
      isCancelled = true;
    };
  }, [apolloClient, getProjectFileCards]);

  const cardCount = resolvedCards.length;

  const {
    index,
    maxIndex,
    dotCount,
    overflowRef,
    trackStyle,
    cardStyle,
    goPrev,
    goNext,
    goTo,
    onTouchStart,
    onTouchEnd,
  } = useCarousel(cardCount);

  const openProjectPreview = (projectId: string | null) => {
    if (!projectId) return;
    setPreviewProjectId(projectId);
    setPreviewOpen(true);
  };

  const closeProjectPreview = () => {
    setPreviewOpen(false);
    setPreviewProjectId(null);
  };

  return (
    <section className="files-section">
      <div className="files-hdr fi">
        <div>
          <div className="ey">Designer Files</div>
          <h2 className="st" style={{ marginBottom: 0 }}>
            Real work. Real designers.
            <br />
            <em>Real results.</em>
          </h2>
        </div>
      </div>

      <div className="carousel-wrap fi">
        <button
          className="carousel-btn c-prev"
          aria-label="Previous designer file"
          disabled={index === 0}
          onClick={goPrev}
        >
          <ArrowLeft />
        </button>

        <div
          className="carousel-overflow"
          ref={overflowRef}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="carousel-track" style={trackStyle}>
            {resolvedCards.map((card) => (
              <div className="cs" style={cardStyle} key={card.key}>
                <article
                  className="fc"
                  role={card.projectId ? "button" : undefined}
                  tabIndex={card.projectId ? 0 : -1}
                  onClick={() => openProjectPreview(card.projectId)}
                  onKeyDown={(event) => {
                    if (!card.projectId) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openProjectPreview(card.projectId);
                    }
                  }}
                  aria-label={
                    card.projectId
                      ? `Open project details for ${card.title}`
                      : `${card.title} project details unavailable`
                  }
                >
                  <img
                    className="fc-img"
                    src={card.imageUrl}
                    alt={`${card.title} project preview`}
                    loading="lazy"
                  />
                  <img
                    className="fc-seal"
                    src={sealImage}
                    alt="HDG Authorized Seal"
                    loading="lazy"
                  />
                  <div className="fc-ov">
                    <div className="fc-id">
                      {card.displayDesignerId ?? "Designer ID unavailable"}
                    </div>
                    <div className="fc-loc" title={card.title}>
                      {card.title}
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        <button
          className="carousel-btn c-next"
          aria-label="Next designer file"
          disabled={index >= maxIndex}
          onClick={goNext}
        >
          <ArrowRight />
        </button>

        <div className="c-dots">
          {Array.from({ length: dotCount }).map((_, dotIndex) => (
            <button
              key={`designer-dot-${dotIndex}`}
              className={`cdot ${dotIndex === index ? "on" : ""}`}
              onClick={() => goTo(dotIndex)}
              aria-label={`Go to slide ${dotIndex + 1}`}
            />
          ))}
        </div>
      </div>

      <ProjectPreviewDialog
        open={previewOpen}
        onClose={closeProjectPreview}
        fileType="project"
        fileId={previewProjectId}
      />

      <div className="files-mem fi">
        <div>
          <div className="fm-ey">HDG Membership Required for Full Access</div>
          <div className="fm-h">
            Unlock hundreds of Designer Files —
            <em>
              before and afters, full project galleries, and expert design
              inspiration.
            </em>
          </div>
          <p className="fm-s">
            HDG Members get exclusive access to the complete Designer Files
            library — real before and afters, room-by-room transformations,
            design case studies, and expert inspiration from HDG authorized
            interior designers across Atlanta and beyond. See the work. Know
            the fit. Hire with total confidence.
          </p>
        </div>
        <Link to={`${Pages.SIGNUP}?userType=Homeowner`} className="btn-rose">
          Join as a Member →
        </Link>
      </div>
    </section>
  );
};
